// common.types.Transcript.ts

import type { SegmentResult } from './Audio';
import type { BaseStorableModel } from './Base';
import type { ExchangeMessage } from './Exchange';
import type { IMediaFile } from './MediaFile';
import type { Meeting } from './Meeting';
import type { BaseModel } from './Sqlite';

export type TranscriptStatus = 'ready' | 'empty' | 'transcribing' | 'transcribed' | 'error';

export const defaultTranscriptStatus: TranscriptStatus = 'ready';

export type TranscriptData = {
  model?: string; // Model id used to generate the transcript
  captureType?: string; // Type of capture flow used (meeting, quick-note, etc.)
};

// Lightweight meeting summary for navigation (without full Meeting object)
// DEPRECATED: Use Meeting type directly instead
export interface MeetingSummary {
  date: Date;
  attendees: string[];
  summary?: string; // Just the summary text, not all minutes data
  title?: string;
}

interface BaseTranscript extends BaseStorableModel {
  _type: 'transcriptSummary' | 'fullTranscript';
  id: string;
  name: string;
  editedName?: string;
  summary?: string; // Denormalized summary for navigation performance (truncated to ~500 chars)
  status: TranscriptStatus;
  duration?: number; // Duration of the transcript in seconds
  eventId?: string; // Back link to parent
  fileExists: boolean;
  file?: IMediaFile | null | undefined;
  isDeleted: boolean;
  isDeleting?: boolean; // Local state for UI feedback
  isFavorite?: boolean;
  workspaceId?: string; // Reference to workspace
  pushedAt?: Date;
  date: Date;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
  userId: string;
  data?: TranscriptData; // Additional data (captureType, etc.)
}

// Lightweight transcript object for navigation performance
// Contains only essential fields needed for filtering and display
export interface TranscriptSummary extends BaseTranscript {
  _type: 'transcriptSummary';
  // Meeting field contains lightweight data (no full rawOutput/editedOutput from minutes)
}

// The transcript object. These fields are fixed.
export interface TranscriptFull extends BaseTranscript {
  _type: 'fullTranscript';
  rawOutput: string[];
  editedOutput: string[];
  output?: TranscriptOutputLineParsedTime[];
  notionMeetingId?: string;
  userNotes?: string; // User-supplied notes in markdown format
  meeting: Meeting | null; // Meeting data (lightweight for summaries, full for TranscriptFull)
  // Meeting field inherited from BaseTranscript contains full Meeting object
}

export type Transcript = TranscriptSummary | TranscriptFull;

// The transcript object. These fields are fixed.
export type TimeData = {
  hours: number;
  minutes: number;
  seconds: number;
  hoursStr: string;
  minutesStr: string;
  secondsStr: string;
};

interface TranscriptOutputLineBase extends Record<string, unknown> {
  isSkeleton?: boolean;
  delta: number;
  vtt: string;
  text: string;
  startSeconds: number;
  endSeconds: number;
  speaker?: SegmentResult['dominantSource'];
}

export interface TranscriptOutputLineParsedTime extends TranscriptOutputLineBase {
  start: TimeData;
  end: TimeData;
}

export interface TranscriptOutputLineRawTime extends TranscriptOutputLineBase {
  start: string;
  end: string;
}

// The transcript object. These fields are fixed.
export type TranscriptOutputLine = TranscriptOutputLineParsedTime | TranscriptOutputLineRawTime;

export type InvokeTranscribeResult = {
  message: string;
  transcriptions: string[];
};

export type TranscriptOutput = TranscriptOutputLineParsedTime[];

export interface IMissingTranscriptError extends Error {
  transcriptId: string;
}

export class MissingTranscriptError extends Error implements IMissingTranscriptError {
  transcriptId: string;

  constructor(transcriptId: string, message?: string) {
    super(message || `No such transcript: ${transcriptId}`);
    this.name = 'MissingTranscriptError';
    this.transcriptId = transcriptId;

    // Ensure the error's prototype chain is correctly set (necessary for extending built-in classes in TypeScript)
    Object.setPrototypeOf(this, MissingTranscriptError.prototype);
  }
}

// Define types and helper functions for converting Transcript <-> DB row
export type TranscriptRow = {
  id: string;
  file: string | null;
  rawOutput: string;
  editedOutput: string;
  data: string | null;
  name: string;
  editedName: string;
  summary?: string | null;
  status: string;
  duration: number | null;
  meeting?: string | null;
  eventId?: string | null;
  fileExists: string | number | boolean; // SQLite stores booleans as string literals or numbers
  isDeleted: string | number | boolean; // SQLite stores booleans as string literals or numbers
  isFavorite: string | number | boolean; // SQLite stores booleans
  notionMeetingId?: string | null;
  output: string;
  userNotes?: string | null;
  workspaceId?: string | null;
  pushedAt: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  date: string;
};

export type TranscriptModel = BaseModel<TranscriptFull & Record<string, unknown>, TranscriptRow>;

export type SkeletonTranscriptOutput = {
  isSkeleton: true;
};
export type DisplayedTranscriptOutput = (
  | TranscriptOutputLineParsedTime
  | SkeletonTranscriptOutput
)[];

export type TranscriptWithMessages = Transcript & { messages: ExchangeMessage[] };

export interface TranscriptionEvent {
  id: string;
  transcriptions: string[];
}
export interface TranscriptionOutputEvent extends TranscriptionEvent {
  index: number;
}

export interface TranscriptionCompleteEvent {
  id?: string | null;
  streaming: boolean;
  message?: string;
  transcriptions?: string[];
}
